import * as React from 'react';
import { useFileSelector } from 'common-hooks';
import { CtaButton, Icon, rem, Spinner, styled, theme } from 'design-system';

type ctaButtonProps = React.ComponentProps<typeof CtaButton>;
type Props = {
  disabled: boolean;
  onClick: () => void;
  onFileSelected: (file: File) => void;
  text: string;
  textStyle?: object;
  isAIPP?: boolean;
  isIcon?: boolean;
  isHover?: boolean;
  isHoverPrimary?: boolean;
  optionalLanguageText?: { processing?: string };
  disableButSpin?: boolean;
  smallSpinner?: boolean;
} & ctaButtonProps;

const CtaButtonStyled = styled(CtaButton, {
  fontSize: '20px',
  border: 'none',
  backgroundColor: theme.colors.blue9,

  variants: {
    isAIPP: {
      true: {
        border: '3px solid white',
      },
    },
    isBlueBack: {
      true: {
        backgroundColor: 'rgba(5, 30, 87, 1)',
        border: '1px solid white',
        borderRadius: '7px',
        color: theme.colors.white,
        transition: 'background-color 0.3s ease, color 0.3s ease',
        '&:hover': {
          backgroundColor: `${theme.colors.white} !important`,
          color: `${theme.colors.blue9} !important`,
        },

        '&:disabled': {
          backgroundColor: theme.colors.white,
          color: theme.colors.blue9,
          cursor: 'not-allowed',
          borderColor: theme.colors.blue9,
        },
      },
    },
    isHoverPrimary: {
      true: {
        '&:hover': {
          backgroundColor: `${theme.colors.blue10} !important`,
        },
        '&:disabled': {
          backgroundColor: `${theme.colors.blue6} !important`,
          color: `${theme.colors.white} !important`,
        },
      },
    },
    isHover: {
      true: {
        '&:hover': {
          backgroundColor: `${theme.colors.blue5} !important`,
          color: `${theme.colors.blue9} !important`,
        },
        '&:disabled': {
          cursor: 'not-allowed',
          backgroundColor: 'inherit !important',
          color: `${theme.colors.blue9} !important`,

          '&:hover': {
            // backgroundColor: 'inherit !important',
          },
        },
      },
    },

    isIcon: {
      true: {
        fontSize: '16px !important',
        borderRadius: '7px',
      },
    },
  },
});

export const UploadButtonNew = ({
  disabled,
  onClick,
  onFileSelected,
  text,
  textStyle = {},
  isBlueBack,
  isAIPP,
  isHoverPrimary,
  isIcon,
  optionalLanguageText,
  disableButSpin,
  smallSpinner = false,
  ...ctaButtonProps
}: Props) => {
  const { buttonProps, inputProps } = useFileSelector({
    fileOptions: {
      supportedExtensions: ['.doc', '.docx', '.pdf'],
      supportedTypes: [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
    },
    onClick,
    onFileSelected,
  });

  return (
    <>
      <CtaButtonStyled
        size={'big'}
        disabled={disabled || disableButSpin}
        isAIPP={isAIPP}
        isBlueBack={isBlueBack}
        isIcon={isIcon}
        isHoverPrimary={isHoverPrimary}
        {...ctaButtonProps}
        {...buttonProps()}
        aria-label="Upload file"
      >
        <ButtonContainer id="Processing-Button">
          {disabled ? (
            <>
              {optionalLanguageText?.processing || 'Processing...'}
              {smallSpinner ? (
                (isBlueBack || isIcon) && !isHoverPrimary ? (
                  <Spinner color={theme.colors.blue9} size={24} />
                ) : (
                  <Spinner color={theme.colors.gray2} size={24} />
                )
              ) : (isBlueBack || isIcon) && !isHoverPrimary ? (
                <Spinner color={theme.colors.blue9} size={32} />
              ) : (
                <Spinner color={theme.colors.gray2} size={32} />
              )}
            </>
          ) : (
            <>
              {(isBlueBack || isIcon) && (
                <Icon
                  name="arrowUpFromBracket"
                  variant={'regular'}
                  height={16}
                />
              )}

              <ButtonContent
                style={textStyle}
                dangerouslySetInnerHTML={{
                  __html: text ?? '',
                }}
              />
            </>
          )}
        </ButtonContainer>
      </CtaButtonStyled>
      <input
        id="fileUpload"
        {...inputProps()}
        onChange={(e) => {
          const func = inputProps().onChange;
          if (func) {
            func(e);
          }
          setTimeout(() => {
            e.target.value = '';
          }, 0);
        }}
        aria-hidden="true"
      />
    </>
  );
};

const ButtonContainer = styled('div', {
  display: 'flex',
  gap: rem(8),
  alignItems: 'center',
  justifyContent: 'center',
});

const ButtonContent = styled('div', {
  '>span': {
    backgroundColor: theme.colors.white,
    color: theme.colors.blue9,
    padding: '2px',
    paddingLeft: '4px',
    paddingRight: '4px',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '500',
  },
});
